import axios from "axios";
import { v4 } from "uuid";
import { Politician, Committee, CongressionalTransaction } from '../models/models';

// Types for API parameters
interface GetPoliticiansParams {
  limit?: number;
  offset?: number;
  party?: string;
  state?: string;
}

interface GetTransactionsParams {
  limit?: number;
  offset?: number;
  year?: number;
  transactionType?: string;
  startDate?: string;
  endDate?: string;
  sort?: string;
  politicianId?: string;
  assetSymbol?: string;
}

interface GetCongressionalSummaryParams {
  year?: number;
  politicianId?: string;
  assetSymbol?: string;
  transactionType?: string;
  startDate?: string;
  endDate?: string;
}

// Get politicians with optional filtering and pagination
export const getPoliticians = async (
  accessToken: string,
  params: GetPoliticiansParams = {}
): Promise<Politician[]> => {
  const searchParams = new URLSearchParams();
  
  if (params.limit) searchParams.append('limit', params.limit.toString());
  if (params.offset) searchParams.append('offset', params.offset.toString());
  if (params.party) searchParams.append('party', params.party);
  if (params.state) searchParams.append('state', params.state);

  const queryString = searchParams.toString();
  const url = `${process.env.REACT_APP_API_URL}/v1/congressional/politicians${queryString ? `?${queryString}` : ''}`;

  const response = await axios.get(url, {
    headers: {
      'Authorization': accessToken,
      'X-Request-ID': v4()
    }
  });

  if (response.status !== 200) {
    throw new Error('Failed to get politicians');
  }

  return response.data as Politician[];
};

// Get committees
export const getCommittees = async (accessToken: string): Promise<Committee[]> => {
  const response = await axios.get(process.env.REACT_APP_API_URL + '/v1/congressional/committees', {
    headers: {
      'Authorization': accessToken,
      'X-Request-ID': v4()
    }
  });

  if (response.status !== 200) {
    throw new Error('Failed to get committees');
  }

  return response.data as Committee[];
};

// Get transactions for a specific politician
export const getPoliticianTransactions = async (
  accessToken: string,
  politicianId: string,
  params: GetTransactionsParams = {}
): Promise<any> => {
  const searchParams = new URLSearchParams();
  
  if (params.limit) searchParams.append('limit', params.limit.toString());
  if (params.offset) searchParams.append('offset', params.offset.toString());
  if (params.year) searchParams.append('year', params.year.toString());
  if (params.transactionType) searchParams.append('transactionType', params.transactionType);
  if (params.startDate) searchParams.append('startDate', params.startDate);
  if (params.endDate) searchParams.append('endDate', params.endDate);

  const queryString = searchParams.toString();
  const url = `${process.env.REACT_APP_API_URL}/v1/congressional/politicians/${politicianId}/transactions${queryString ? `?${queryString}` : ''}`;

  const response = await axios.get(url, {
    headers: {
      'Authorization': accessToken,
      'X-Request-ID': v4()
    }
  });

  if (response.status !== 200) {
    throw new Error('Failed to get politician transactions');
  }

  return response.data;
};

// Get transactions for a specific stock/asset
export const getStockTransactions = async (
  accessToken: string,
  symbol: string,
  params: GetTransactionsParams = {}
): Promise<any> => {
  const searchParams = new URLSearchParams();
  
  if (params.limit) searchParams.append('limit', params.limit.toString());
  if (params.offset) searchParams.append('offset', params.offset.toString());
  if (params.year) searchParams.append('year', params.year.toString());
  if (params.transactionType) searchParams.append('transactionType', params.transactionType);
  if (params.startDate) searchParams.append('startDate', params.startDate);
  if (params.endDate) searchParams.append('endDate', params.endDate);

  const queryString = searchParams.toString();
  const url = `${process.env.REACT_APP_API_URL}/v1/congressional/stocks/${symbol}/transactions${queryString ? `?${queryString}` : ''}`;

  const response = await axios.get(url, {
    headers: {
      'Authorization': accessToken,
      'X-Request-ID': v4()
    }
  });

  if (response.status !== 200) {
    throw new Error('Failed to get stock transactions');
  }

  return response.data;
};

// Get all congressional transactions
export const getAllCongressionalTransactions = async (
  accessToken: string,
  params: GetTransactionsParams = {}
): Promise<any> => {
  const searchParams = new URLSearchParams();
  
  if (params.limit) searchParams.append('limit', params.limit.toString());
  if (params.offset) searchParams.append('offset', params.offset.toString());
  if (params.politicianId) searchParams.append('politicianId', params.politicianId);
  if (params.assetSymbol) searchParams.append('assetSymbol', params.assetSymbol);
  if (params.transactionType) searchParams.append('transactionType', params.transactionType);
  if (params.startDate) searchParams.append('startDate', params.startDate);
  if (params.endDate) searchParams.append('endDate', params.endDate);
  if (params.sort) searchParams.append('sort', params.sort);

  const queryString = searchParams.toString();
  const url = `${process.env.REACT_APP_API_URL}/v1/congressional/transactions${queryString ? `?${queryString}` : ''}`;

  const response = await axios.get(url, {
    headers: {
      'Authorization': accessToken,
      'X-Request-ID': v4()
    }
  });

  if (response.status !== 200) {
    throw new Error('Failed to get congressional transactions');
  }

  return response.data;
};

// Get congressional summary/analytics
export const getCongressionalSummary = async (
  accessToken: string,
  params: GetCongressionalSummaryParams = {}
): Promise<any> => {
  const searchParams = new URLSearchParams();
  
  if (params.year) searchParams.append('year', params.year.toString());
  if (params.politicianId) searchParams.append('politicianId', params.politicianId);
  if (params.assetSymbol) searchParams.append('assetSymbol', params.assetSymbol);

  const queryString = searchParams.toString();
  const url = `${process.env.REACT_APP_API_URL}/v1/congressional/analytics/summary${queryString ? `?${queryString}` : ''}`;

  const response = await axios.get(url, {
    headers: {
      'Authorization': accessToken,
      'X-Request-ID': v4()
    }
  });

  if (response.status !== 200) {
    throw new Error('Failed to get congressional summary');
  }

  return response.data;
};
